/**
 * `/credits` — current balance + ledger history.
 *
 * Renders one of two views depending on session kind:
 *   - User sessions: weekly grant, reservation, ledger history.
 *   - Demo sessions: per-session cards remaining, shared pool remaining
 *     (multi_use only), session expiry. No history (demos don't accrue
 *     ledger entries).
 */

import { useEffect, useState } from 'react';
import type { CreditBalance, CreditLedgerEntry, DemoCreditBalance, UserCreditBalance } from '@eloquor/api-contract';
import { AppShell } from '../components/layout/AppShell';
import { getMyBalance, getMyHistory } from '../api/credits';
import { useSession } from '../hooks/useSession';

export function CreditsPage() {
  const { isDemo } = useSession();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [entries, setEntries] = useState<CreditLedgerEntry[]>([]);
  useEffect(() => {
    void getMyBalance().then(setBalance);
    if (!isDemo) {
      void getMyHistory().then((r) => setEntries(r.entries));
    }
  }, [isDemo]);

  return (
    <AppShell>
      <div className="auth-eyebrow"><span className="num">03</span> · Credits</div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        Your <em>budget</em>.
      </h1>

      {!balance ? (
        <div style={{ color: 'var(--fg-3)' }}>Loading…</div>
      ) : balance.kind === 'demo' ? (
        <DemoBudget balance={balance} />
      ) : (
        <UserBudget balance={balance} entries={entries} />
      )}
    </AppShell>
  );
}

function UserBudget({
  balance,
  entries,
}: {
  balance: UserCreditBalance;
  entries: CreditLedgerEntry[];
}) {
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <Stat
          label="Available"
          value={balance.available.toLocaleString()}
          sub={`${balance.reserved} reserved`}
        />
        <Stat
          label="Weekly grant"
          value={balance.weeklyCardGrant.toLocaleString()}
          sub={`cards / week`}
        />
        <Stat
          label="Next reset"
          value={new Date(balance.nextResetAt).toLocaleString()}
          sub="00:00 UTC Wednesdays"
        />
      </div>

      <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 18, margin: '32px 0 12px' }}>
        Recent activity
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <th style={{ padding: '8px 12px' }}>When</th>
            <th style={{ padding: '8px 12px' }}>Reason</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', color: 'var(--fg-3)' }}>
                {new Date(e.createdAt).toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px' }}>{e.reason.replace(/_/g, ' ')}</td>
              <td
                style={{
                  padding: '10px 12px',
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  color: e.delta >= 0 ? 'var(--rt-ok-ink)' : 'var(--rt-error-ink)',
                }}
              >
                {e.delta >= 0 ? '+' : ''}{e.delta}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DemoBudget({ balance }: { balance: DemoCreditBalance }) {
  const multi = balance.linkKind === 'multi_use';
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 16, maxWidth: 640 }}>
        You're in a {multi ? 'shared ' : ''}demo session. {multi
          ? 'Many users can be in this demo at once; your per-session grant runs out first, then the shared pool.'
          : 'No weekly resets, no ledger — just a fixed card budget for the lifetime of this demo.'}
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: multi ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <Stat
          label={multi ? 'Your session' : 'Cards remaining'}
          value={balance.sessionRemaining.toLocaleString()}
          sub={`of ${balance.sessionGrant.toLocaleString()} granted`}
        />
        {multi && (
          <Stat
            label="Shared pool"
            value={balance.poolRemaining.toLocaleString()}
            sub={`of ${balance.poolGrant.toLocaleString()} across all sessions`}
          />
        )}
        <Stat
          label="Session ends"
          value={new Date(balance.expiresAt).toLocaleString()}
          sub={multi ? 'all sessions expire together' : 'fixed deadline'}
        />
      </div>
    </>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div
      style={{
        background: 'var(--vellum)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '16px 20px',
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 28, color: 'var(--fg-1)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>{sub}</div>
    </div>
  );
}
